/**
 * Specification draft — the road in when the claims and drawings
 * already exist.
 *
 * A firm arrives with the claim set and the figures done and wants only
 * the specification written. Four steps: the disclosure, the claims,
 * the drawings, then hand over to the Specification page, which is
 * unchanged and cannot tell which road the matter came in by.
 *
 * This is a CONFIRM surface, not an editor. The drafter reads what was
 * parsed, corrects the few fields that matter, and confirms. Real
 * editing lives where it already lives: the Drawing workbench for
 * figures, the Spec page for paragraphs. Anything that would duplicate
 * those is deliberately absent — a second half-built editor is worse
 * than a link to the real one.
 *
 * Progress is read from the server on every step (GET /status derives
 * it from the rows), so closing the tab and coming back next week
 * resumes exactly where it left off.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  deleteSpecIntakeFigure, getJobStatus, getSpecIntakeFigures,
  getSpecIntakeStatus, patchSpecIntakeDigest, patchSpecIntakeFigure,
  postSpecIntakeClaimsCommit, postSpecIntakeClaimsParse,
  postSpecIntakeClaimsRevise, SpecIntakeRevision,
  SpecIntakeRevisionParagraph,
  postSpecIntakeClaimsParseFile, postSpecIntakeClaimsQc,
  postSpecIntakeDigest, postSpecIntakeFigureUpload,
  postSpecIntakeFiguresImport, postSpecIntakeFreezeAll,
  postSpecIntakeReadSigns, postSpecIntakeSignsCommit,
  postSpecIntakeSignsParse, specIntakeImageUrl,
  SpecIntakeClaimsParse, SpecIntakeFigure, SpecIntakeFigures,
  SpecIntakeParsedClaim, SpecIntakeQcFinding, SpecIntakeReadSign,
  SpecIntakeSimilarSigns, SpecIntakeStatus, uploadIdf,
} from '@/api/client';
import AppFooter from '@/components/layout/AppFooter';
import CoursesLink from '@/components/layout/CoursesLink';
import { describeApiError, DescribedApiError } from '@/lib/describeApiError';

interface Props {
  matterId: string;
  onBack?: () => void;
  /** Hand over to the Specification page once the intake is ready. */
  onOpenSpec?: () => void;
  /** The Drawing workbench, for anything beyond confirming a figure. */
  onOpenDrawings?: () => void;
  /** The user-guide videos (the Courses link). */
  onOpenCourses?: () => void;
}

type StepKey = 'idf' | 'claims' | 'drawings' | 'ready';

/** A reference sign awaiting registration. ``key`` stays put while the
 * drafter corrects the numeral (NB-35). */
type PendingSign = SpecIntakeReadSign & { key: string };

/** Does the drafter's name for a known numeral differ from the
 * registered one? Recomputed as they type. */
const clashes = (s: PendingSign): boolean => (
  s.known && !!s.existing_label && !!s.label.trim()
  && s.label.trim().toLowerCase() !== s.existing_label.trim().toLowerCase()
);

const DIGEST_FIELDS = ['overview', 'problem', 'solution', 'implementation'] as const;
type DigestField = typeof DIGEST_FIELDS[number];

const FIELD_LABEL: Record<string, string> = {
  overview: 'overview', problem: 'problem', solution: 'solution',
  implementation: 'implementation', technical_features: 'technical features',
};

const STEPS: Array<{ key: StepKey; title: string; blurb: string }> = [
  { key: 'idf', title: 'Disclosure',
    blurb: 'What the invention is. Every drafting prompt reads it.' },
  { key: 'claims', title: 'Claims',
    blurb: 'The claim set the specification will be written against.' },
  { key: 'drawings', title: 'Drawings',
    blurb: 'The figures and the reference signs they use.' },
  { key: 'ready', title: 'Start',
    blurb: 'Hand over to the specification.' },
];

const outlineBtn =
  'inline-flex h-9 items-center justify-center rounded border border-input ' +
  'bg-background px-3 text-xs font-medium hover:bg-accent disabled:opacity-50';
const primaryBtn =
  'inline-flex h-9 items-center justify-center rounded-md bg-primary px-4 ' +
  'text-xs font-medium text-primary-foreground hover:bg-primary/90 ' +
  'disabled:opacity-50';
const inputClass =
  'w-full rounded border border-zinc-300 bg-white px-2 py-1.5 text-[13px] ' +
  'leading-5 text-zinc-800 focus:outline-none focus:ring-2 focus:ring-zinc-300';

const ERROR_HEADLINE: Record<string, string> = {
  cost_cap: 'AI spend cap reached',
  timeout: 'The request timed out',
  network: 'Could not reach the server',
  provider: 'The AI provider is unavailable',
  validation: 'That was rejected',
  not_found: 'Not found',
  conflict: 'Conflicting change',
  permission: 'Not permitted',
  unknown: 'Something went wrong',
};

export default function SpecIntakePage({
  matterId, onBack, onOpenSpec, onOpenDrawings, onOpenCourses,
}: Props) {
  const [status, setStatus] = useState<SpecIntakeStatus | null>(null);
  const [step, setStep] = useState<StepKey>('idf');
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<DescribedApiError | null>(null);
  const [note, setNote] = useState<string | null>(null);

  // Step 2 — the parsed claim set awaiting confirmation, plus the
  // optional check's findings.
  const [parsed, setParsed] = useState<SpecIntakeClaimsParse | null>(null);
  const [claimsText, setClaimsText] = useState('');
  const [qc, setQc] = useState<SpecIntakeQcFinding[] | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  // Revising confirmed claims with the client's changes: the reader
  // reopens, and a read set is compared rather than committed.
  const [revising, setRevising] = useState(false);
  const [revision, setRevision] = useState<SpecIntakeRevision | null>(null);

  // Step 3 — the figures, and the reference signs awaiting
  // confirmation. Signs reach the pending list two ways: read off a
  // drawing, or pasted as a legend. Both land here so confirming is
  // one act either way.
  const [legendText, setLegendText] = useState('');
  const [signs, setSigns] = useState<PendingSign[] | null>(null);
  const [signsFrom, setSignsFrom] = useState<string>('');
  // NB-33 — the figure the pending signs were read off; they are
  // recorded on it when registered. Null for a pasted legend.
  const [signsFigureId, setSignsFigureId] = useState<string | null>(null);
  // NB-36 — numerals naming one element, as the last read reported.
  const [similar, setSimilar] = useState<SpecIntakeSimilarSigns[]>([]);
  const [figures, setFigures] = useState<SpecIntakeFigures | null>(null);
  const [picked, setPicked] = useState<number[]>([]);
  const [freezeReport, setFreezeReport] =
    useState<Awaited<ReturnType<typeof postSpecIntakeFreezeAll>> | null>(null);
  const idfFileRef = useRef<HTMLInputElement | null>(null);
  const figFileRef = useRef<HTMLInputElement | null>(null);

  // Step 1 — what the drafter is typing into the digest, ahead of the
  // save on blur, so Resynthesize can send a field still being edited.
  const [drafts, setDrafts] = useState<Partial<Record<DigestField, string>>>({});
  const [featureDraft, setFeatureDraft] = useState('');
  // Reading again from the document discards corrections; it asks first.
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  // Additional information from the client: a file, or a pasted note.
  const [adding, setAdding] = useState(false);
  const [noteText, setNoteText] = useState('');
  const addFileRef = useRef<HTMLInputElement | null>(null);

  const load = useCallback(async () => {
    try {
      const s = await getSpecIntakeStatus(matterId);
      setStatus(s);
      return s;
    } catch (e: unknown) {
      setErr(describeApiError(e));
      return null;
    }
  }, [matterId]);

  const loadFigures = useCallback(async () => {
    try {
      setFigures(await getSpecIntakeFigures(matterId));
    } catch (e: unknown) {
      setErr(describeApiError(e));
    }
  }, [matterId]);

  useEffect(() => {
    // Land on the first step that still needs the drafter, so a
    // resumed intake does not make them click past finished work.
    void (async () => {
      const s = await load();
      if (!s) return;
      if (!s.steps.idf.done) setStep('idf');
      else if (!s.steps.claims.done) setStep('claims');
      else if (!s.steps.drawings.done) setStep('drawings');
      else setStep('ready');
    })();
  }, [load]);

  const run = async (key: string, fn: () => Promise<void>) => {
    setBusy(key);
    setErr(null);
    setNote(null);
    try {
      await fn();
    } catch (e: unknown) {
      setErr(describeApiError(e));
    } finally {
      setBusy(null);
    }
  };

  const done = useMemo(() => ({
    idf: !!status?.steps.idf.done,
    claims: !!status?.steps.claims.done,
    drawings: !!status?.steps.drawings.done,
    ready: !!status?.ready.done,
  }), [status]);

  // Keep the figure list in step with the rest of the page: it is
  // loaded when the step opens, and again after anything that changes
  // it. Deriving it rather than caching is what lets the intake be
  // left and resumed.
  useEffect(() => {
    if (step === 'drawings') void loadFigures();
  }, [step, loadFigures]);

  // ── Step 1 — the disclosure ───────────────────────────────────
  // ``add`` is more from the client, kept alongside the disclosure;
  // otherwise the file is the disclosure, replacing any there is.
  const uploadDisclosure = (file: File, add = false) =>
    run(add ? 'idf-add' : 'idf-upload', async () => {
    // What "read in" means is measured against the count before this
    // upload, not against zero: a second document on a matter that
    // already has one would otherwise look read in the instant it
    // landed, and the digest would run against the old one only.
    const prior = (await load())?.steps.idf;
    const before = prior?.indexed ?? 0;
    // A disclosure is already there, so this one replaces it: the old
    // one is archived and the digest has to be read again. The button
    // says "Replace", which is the drafter's confirmation.
    const replacing = !add && (prior?.documents ?? 0) > 0;
    const out = await uploadIdf(
      matterId, file, 'auto', add ? 'supplementary' : 'idf', replacing);
    // Uploaded is not the same as readable. Extraction and indexing
    // finish after the upload returns — on the worker path (202) and
    // on the inline path alike, where the response comes back before
    // the background indexing task has run. Reading the disclosure
    // before then digests an empty document and the drafting prompts
    // end up with no invention at all, so wait for it either way.
    setNote('Uploaded. Reading it in…');
    const deadline = Date.now() + 180_000;
    const jobId = out.status === 202
      ? (out.data as { job_id: string }).job_id : null;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, 1500));
      if (jobId) {
        // eslint-disable-next-line no-await-in-loop
        const job = await getJobStatus(jobId);
        if (job.status === 'failed') {
          throw new Error(job.error || 'Reading in the disclosure failed.');
        }
        if (job.status === 'finished') break;
      } else {
        // eslint-disable-next-line no-await-in-loop
        const s = await load();
        // Replacing archives the old document, so the indexed count
        // goes back to where it was rather than up: wait instead for
        // every document still on the matter to be read in.
        if (s && (replacing
          ? s.steps.idf.documents > 0
            && s.steps.idf.indexed === s.steps.idf.documents
          : s.steps.idf.indexed > before)) break;
      }
      if (Date.now() > deadline) {
        throw new Error(
          'The disclosure is still being read in. Reopen this page in a '
          + 'moment — the upload is not lost.',
        );
      }
    }
    await load();
    setNote(add
      ? 'Added. Resynthesize to fold it into the digest — your '
        + 'corrections are kept.'
      : replacing
        ? 'Replaced. Read it next — the digest below still describes the '
          + 'disclosure it replaced.'
        : 'Uploaded. Read it next, so the drafting prompts have the '
          + 'invention to work from.');
    if (add) {
      setAdding(false);
      setNoteText('');
    }
  });

  // A pasted note becomes a dated text file, so it is read, indexed
  // and kept exactly like a document the client emailed.
  const addNote = () => {
    const text = noteText.trim();
    if (!text) return;
    const stamp = new Date().toISOString().slice(0, 16).replace(/[:T]/g, '-');
    void uploadDisclosure(
      new File([text], `client-note-${stamp}.txt`, { type: 'text/plain' }),
      true,
    );
  };

  const savedDigest = status?.steps.idf.digest ?? null;
  const editedFields = status?.steps.idf.edited_fields ?? [];
  // Fields typed into but not yet saved (the save runs on blur).
  const unsaved = useMemo(() => {
    const out: Partial<Record<DigestField, string>> = {};
    if (!savedDigest) return out;
    for (const f of DIGEST_FIELDS) {
      const d = drafts[f];
      if (d !== undefined && d !== savedDigest[f]) out[f] = d;
    }
    return out;
  }, [drafts, savedDigest]);
  const hasCorrections = editedFields.length > 0
    || Object.keys(unsaved).length > 0;
  const newMaterial = status?.steps.idf.new_material ?? [];
  const canResynthesize = hasCorrections || newMaterial.length > 0;

  // A reload drops only the drafts the server now holds. Clearing them
  // all would wipe a field being typed into while the save of the
  // field just left comes back.
  useEffect(() => {
    if (!savedDigest) return;
    setDrafts((d) => {
      const kept: Partial<Record<DigestField, string>> = {};
      for (const f of DIGEST_FIELDS) {
        if (d[f] !== undefined && d[f] !== savedDigest[f]) kept[f] = d[f];
      }
      return kept;
    });
  }, [savedDigest]);

  const runDigest = (force = false) => {
    if (hasCorrections && !force) {
      setConfirmDiscard(true);
      return;
    }
    setConfirmDiscard(false);
    void run('digest', async () => {
      await postSpecIntakeDigest(matterId);
      setDrafts({});
      await load();
      setNote('Read. Check it below before moving on — every drafting '
        + 'prompt works from this.');
    });
  };

  const resynthesize = () => run('resynthesize', async () => {
    setConfirmDiscard(false);
    await postSpecIntakeDigest(matterId, {
      keepEdits: true,
      corrections: Object.keys(unsaved).length ? unsaved : undefined,
    });
    setDrafts({});
    await load();
    setNote(hasCorrections
      ? 'Resynthesized. Your corrections are kept as you wrote them; '
        + 'the rest of the digest, the technical features and the '
        + 'technology area were re-read to agree with them.'
      : 'Resynthesized with everything the client has sent.');
  });

  const saveDigestField = (field: string, value: string) =>
    run('digest-patch', async () => {
      await patchSpecIntakeDigest(matterId, { [field]: value } as any);
      await load();
    });

  const saveFeatures = (features: string[]) =>
    run('digest-patch', async () => {
      await patchSpecIntakeDigest(matterId, { technical_features: features });
      await load();
    });

  const addFeature = () => {
    const f = featureDraft.trim();
    if (!f || !savedDigest) return;
    setFeatureDraft('');
    if (savedDigest.technical_features
      .some((x) => x.toLowerCase() === f.toLowerCase())) return;
    void saveFeatures([...savedDigest.technical_features, f]);
  };

  // ── Step 2 — the claims ───────────────────────────────────────
  const parseText = () => run('parse', async () => {
    setQc(null);
    setRevision(null);
    setParsed(await postSpecIntakeClaimsParse(matterId, claimsText));
  });

  const parseFile = (file: File) => run('parse', async () => {
    setQc(null);
    setRevision(null);
    const out = await postSpecIntakeClaimsParseFile(matterId, file);
    setParsed(out);
  });

  const runQc = () => run('qc', async () => {
    if (!parsed?.claims?.length) return;
    const out = await postSpecIntakeClaimsQc(matterId, parsed.claims);
    setQc(out.findings || []);
  });

  const commitClaims = () => run('commit', async () => {
    if (!parsed?.claims?.length) return;
    await postSpecIntakeClaimsCommit(matterId, parsed.claims);
    setParsed(null);
    setQc(null);
    setClaimsText('');
    await load();
    setStep('drawings');
    setNote('Claims confirmed and frozen. The drawings step is open.');
  });

  const startRevising = () => {
    setRevising(true);
    setParsed(null);
    setQc(null);
    setRevision(null);
    setClaimsText('');
  };

  const stopRevising = () => {
    setRevising(false);
    setParsed(null);
    setQc(null);
    setRevision(null);
    setClaimsText('');
  };

  const compareRevision = () => run('revise-preview', async () => {
    if (!parsed?.claims?.length) return;
    setRevision(await postSpecIntakeClaimsRevise(matterId, parsed.claims, false));
  });

  const applyRevision = () => run('revise-apply', async () => {
    if (!parsed?.claims?.length) return;
    const out = await postSpecIntakeClaimsRevise(matterId, parsed.claims, true);
    const toReview = out.changed.reduce((n, c) => n + c.paragraphs.length, 0);
    stopRevising();
    await load();
    setNote(
      'Claims revised in place.'
      + (toReview ? ` ${toReview} paragraph${toReview === 1 ? '' : 's'} `
        + 'written for a changed claim to review — listed below.' : '')
      + (out.added.length ? ' New claims show as not yet covered on the '
        + 'Specification page; Cover missing drafts them.' : ''),
    );
  });

  // ── Step 3 — the drawings ─────────────────────────────────────
  const importPicked = (ids: number[]) => run('import', async () => {
    if (!ids.length) return;
    const out = await postSpecIntakeFiguresImport(matterId, ids);
    setPicked([]);
    await Promise.all([loadFigures(), load()]);
    const failed = out.failed.length;
    setNote(
      `${out.imported.length} figure${out.imported.length === 1 ? '' : 's'} added`
      + (failed
        ? `. ${failed} could not be: `
          + out.failed.map((f) => f.reason).join('; ')
        : '. Give each one a description, then confirm below.'),
    );
  });

  const uploadFigure = (file: File) => run('fig-upload', async () => {
    await postSpecIntakeFigureUpload(matterId, file);
    await Promise.all([loadFigures(), load()]);
    setNote('Added. It needs a label and a one-line description before '
      + 'it can be confirmed.');
  });

  const discardFigure = (figure: SpecIntakeFigure) =>
    run(`discard-${figure.id}`, async () => {
      await deleteSpecIntakeFigure(matterId, figure.id);
      await Promise.all([loadFigures(), load()]);
      setNote(`${figure.fig_label || 'The figure'} was removed from the `
        + 'drawings. The disclosure image it came from is untouched.');
    });

  const saveFigure = (
    figureId: string, body: { fig_label?: string; description?: string },
  ) => run(`patch-${figureId}`, async () => {
    await patchSpecIntakeFigure(matterId, figureId, body);
    await Promise.all([loadFigures(), load()]);
  });

  const readSigns = (figure: SpecIntakeFigure) =>
    run(`signs-${figure.id}`, async () => {
      const out = await postSpecIntakeReadSigns(matterId, figure.id);
      if (out.error) {
        setErr({ kind: 'provider', retryable: true,
          message: `${out.error} — paste the legend below instead.` });
        return;
      }
      // NB-33 — signs the registry already has stay in the list:
      // registering them records them on this figure, which is what
      // the figure checks and the specification read.
      setSigns(out.signs.map((s, i) => ({ ...s, key: `${s.numeral}-${i}` })));
      setSignsFrom(out.figure);
      setSignsFigureId(out.figure_id || figure.id);
      setSimilar(out.similar || []);
      const unnamed = out.signs.filter((s) => !s.label).map((s) => s.numeral);
      const known = out.signs.filter((s) => s.known && !s.conflict).length;
      setNote(out.signs.length
        ? `${out.signs.length} reference sign`
          + `${out.signs.length === 1 ? '' : 's'} read off ${out.figure}`
          + (known ? ` (${known} already registered)` : '')
          + '. Check them against the drawing, correct any name or number '
          + 'below, then register them.'
          + (unnamed.length
            ? ` ${unnamed.length} had a number but no legible name `
              + `(${unnamed.join(', ')}) — type the name in, or add it `
              + 'through the legend.'
            : '')
        : `No reference signs could be read off ${out.figure}.`);
    });

  const editSign = (key: string, patch: { numeral?: string; label?: string }) => {
    setSigns((prev) => (prev || []).map((s) => {
      if (s.key !== key) return s;
      const next = { ...s, ...patch };
      if (patch.numeral !== undefined && patch.numeral.trim() !== s.numeral) {
        // A different numeral: what the registry holds for the old
        // one no longer applies. The server reports any clash.
        next.known = false;
        next.existing_label = '';
      }
      next.conflict = clashes(next);
      next.unlabeled = !next.label.trim();
      return next;
    }));
  };

  const dropSign = (key: string) => {
    setSigns((prev) => (prev || []).filter((s) => s.key !== key));
  };

  const parseLegend = () => run('legend', async () => {
    const out = await postSpecIntakeSignsParse(matterId, legendText);
    setSigns(out.signs.map((s, i) => ({
      ...s, known: false, existing_label: '', conflict: false,
      key: `${s.numeral}-${i}`,
    })));
    setSignsFrom('the legend');
    setSignsFigureId(null);
    setSimilar([]);
    if (!out.ok) {
      setErr({ kind: 'validation', retryable: false,
        message: out.errors.join(' ') });
    }
  });

  const commitLegend = () => run('legend-commit', async () => {
    // Only the named ones. A numeral without a name is not a reference
    // sign yet, and the server would refuse it anyway.
    const named = (signs || [])
      .map((s) => ({ numeral: s.numeral.trim(), label: s.label.trim() }))
      .filter((s) => s.numeral && s.label);
    if (!named.length) return;
    const out = signsFigureId
      ? await postSpecIntakeSignsCommit(matterId, named, signsFigureId)
      : await postSpecIntakeSignsCommit(matterId, named);
    setSigns(null);
    setSignsFrom('');
    setSignsFigureId(null);
    setSimilar([]);
    setLegendText('');
    await Promise.all([loadFigures(), load()]);
    setNote(
      `${out.added} reference sign${out.added === 1 ? '' : 's'} registered`
      + (out.unchanged ? `, ${out.unchanged} already there` : '')
      + (out.figure && out.placed
        ? `; ${out.placed} recorded on ${out.figure}` : '')
      + (out.bound
        ? `; ${out.bound} claim term${out.bound === 1 ? '' : 's'} `
          + 'matched to a sign' : '')
      + (out.conflicts.length
        ? `. ${out.conflicts.length} numeral(s) already registered with a `
          + 'different label were left as they are: '
          + out.conflicts.map((c) => `${c.numeral} (${c.existing_label})`).join(', ')
        : '.')
      + ((out.rejected || []).length
        ? ' Not registered: '
          + (out.rejected || []).map((r) => `${r.numeral} (${r.reason})`).join('; ')
          + '.'
        : ''),
    );
  });

  const freezeAll = (acknowledge: boolean) => run('freeze', async () => {
    const out = await postSpecIntakeFreezeAll(matterId, acknowledge);
    setFreezeReport(out);
    await Promise.all([loadFigures(), load()]);
  });

  const stepIndex = STEPS.findIndex((s) => s.key === step);
  const registrable = (signs || [])
    .filter((s) => s.label.trim() && s.numeral.trim()).length;

  return (
    <div
      data-testid="spec-intake-page"
      className="max-w-5xl mx-auto p-6 space-y-5"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          {onBack && (
            <button onClick={onBack} aria-label="Back"
                    className="text-zinc-500 hover:text-zinc-900">←</button>
          )}
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold text-zinc-900 truncate"
                style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              Specification draft
            </h1>
            <p className="text-xs text-zinc-500">
              {status?.matter.title || '…'}
              {status ? ` · ${status.matter.jurisdiction}` : ''}
              {' · '}the claims and drawings already exist; only the
              {' '}specification is drafted here
            </p>
          </div>
        </div>
        <CoursesLink onOpenCourses={onOpenCourses} testId="spec-intake-courses-button" />
      </div>

      {/* Step rail */}
      <ol className="grid grid-cols-4 gap-2" data-testid="intake-steps">
        {STEPS.map((s, i) => {
          const isDone = done[s.key];
          const active = s.key === step;
          return (
            <li key={s.key}>
              <button
                type="button"
                data-testid={`intake-step-${s.key}`}
                data-done={isDone ? 'true' : 'false'}
                aria-current={active ? 'step' : undefined}
                onClick={() => setStep(s.key)}
                className={
                  'w-full text-left rounded-md border px-3 py-2 transition-colors '
                  + (active
                    ? 'border-zinc-400 bg-white'
                    : 'border-zinc-200 bg-zinc-50 hover:bg-white')
                }
              >
                <div className="flex items-center gap-2">
                  <span className={
                    'h-5 w-5 shrink-0 rounded-full text-[11px] font-semibold '
                    + 'flex items-center justify-center border '
                    + (isDone
                      ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                      : 'bg-white border-zinc-300 text-zinc-500')
                  }>
                    {isDone ? '✓' : i + 1}
                  </span>
                  <span className="text-sm font-medium text-zinc-900">
                    {s.title}
                  </span>
                </div>
                <div className="mt-0.5 text-[11px] leading-4 text-zinc-500">
                  {s.blurb}
                </div>
              </button>
            </li>
          );
        })}
      </ol>

      {err && (
        <div
          role="alert"
          data-testid="intake-error"
          data-error-kind={err.kind}
          className={
            err.kind === 'cost_cap'
              ? 'rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900'
              : 'rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800'
          }
        >
          <div className="font-medium">{ERROR_HEADLINE[err.kind]}</div>
          <div className="mt-0.5">{err.message}</div>
        </div>
      )}
      {note && !err && (
        <div
          role="status"
          data-testid="intake-note"
          className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900"
        >
          {note}
        </div>
      )}

      {/* ── Step 1 — disclosure ─────────────────────────────────── */}
      {step === 'idf' && status && (
        <section className="space-y-3" data-testid="intake-idf">
          <p className="text-sm text-zinc-600">
            Upload the invention disclosure, then read it. This is what
            every drafting prompt uses as the disclosure — without it the
            model refuses to define terms it cannot support. When the
            client sends more, add it here and resynthesize; to fix the
            reading itself, correct it below.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <input
              ref={idfFileRef}
              type="file"
              accept=".docx,.pdf,.txt,.md,.pptx"
              className="hidden"
              data-testid="intake-idf-file"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void uploadDisclosure(f);
                e.target.value = '';
              }}
            />
            {status.steps.idf.documents > 0 && (
              <button
                type="button"
                className={primaryBtn}
                data-testid="intake-add-info"
                disabled={busy !== null}
                aria-expanded={adding}
                onClick={() => setAdding((a) => !a)}
                title={'A further document or note from the client. It is '
                  + 'kept alongside the disclosure and read with it.'}
              >
                {busy === 'idf-add' ? 'Adding…' : 'Add information from the client'}
              </button>
            )}
            <button
              type="button"
              className={outlineBtn}
              data-testid="intake-upload-idf"
              disabled={busy !== null || status.steps.claims.frozen}
              title={status.steps.claims.frozen
                ? 'The claims are confirmed, so the disclosure can no longer '
                  + 'be replaced here. Correct the reading below instead.'
                : status.steps.idf.documents > 0
                  ? 'Upload a revised disclosure. The current one is archived '
                    + 'and the digest has to be read again.'
                  : 'Upload the invention disclosure'}
              onClick={() => idfFileRef.current?.click()}
            >
              {busy === 'idf-upload' ? 'Uploading…'
                : status.steps.idf.documents > 0
                  ? 'Replace the disclosure' : 'Upload the disclosure'}
            </button>
            <button
              type="button"
              data-testid="intake-run-digest"
              className={status.steps.idf.done ? outlineBtn : primaryBtn}
              disabled={busy !== null || status.steps.idf.indexed === 0}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => runDigest()}
              title={status.steps.idf.documents === 0
                ? 'Upload the disclosure first'
                : status.steps.idf.indexed === 0
                  ? 'The disclosure is still being read in'
                  : status.steps.idf.done
                    ? 'Read the disclosure again from scratch. Your '
                      + 'corrections below are discarded.'
                    : 'Read the uploaded disclosure'}
            >
              {busy === 'digest' ? 'Reading…'
                : status.steps.idf.done ? 'Read it again' : 'Read the disclosure'}
            </button>
            <span className="text-xs text-zinc-500">
              {status.steps.idf.documents} document
              {status.steps.idf.documents === 1 ? '' : 's'} uploaded
              {status.steps.idf.documents > status.steps.idf.indexed
                ? ` · ${status.steps.idf.documents - status.steps.idf.indexed} `
                  + 'still being read in'
                : ''}
              {status.steps.idf.tech_bucket
                ? ` · ${status.steps.idf.tech_bucket.toLowerCase()}` : ''}
            </span>
          </div>

          {adding && (
            <div className="space-y-2 rounded-md border border-zinc-200 bg-zinc-50 p-3"
                 data-testid="intake-add-panel">
              <input
                ref={addFileRef}
                type="file"
                accept=".docx,.pdf,.txt,.md,.pptx"
                className="hidden"
                data-testid="intake-add-file"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void uploadDisclosure(f, true);
                  e.target.value = '';
                }}
              />
              <div className="flex flex-wrap items-center gap-3">
                <button type="button" className={outlineBtn}
                        disabled={busy !== null}
                        onClick={() => addFileRef.current?.click()}>
                  Add a file
                </button>
                <span className="text-xs text-zinc-500">
                  or paste what the client said:
                </span>
              </div>
              <textarea
                className={inputClass}
                rows={4}
                data-testid="intake-add-note"
                placeholder="e.g. The pump may also be driven hydraulically; the controller can use a predicted route instead of a fixed threshold."
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
              />
              <div className="flex items-center gap-3">
                <button type="button" className={outlineBtn}
                        data-testid="intake-add-note-save"
                        disabled={busy !== null || !noteText.trim()}
                        onClick={addNote}>
                  Add note
                </button>
                <span className="text-[11px] text-zinc-500">
                  Kept as a dated note with the disclosure. Nothing changes
                  until you resynthesize.
                </span>
              </div>
            </div>
          )}

          {(status.steps.idf.files?.length ?? 0) > 0 && (
            <ul className="space-y-0.5 text-xs text-zinc-600"
                data-testid="intake-files">
              {status.steps.idf.files!.map((f) => (
                <li key={f.id} className="flex flex-wrap items-center gap-2">
                  <span className="text-[10px] uppercase tracking-wide text-zinc-400">
                    {f.kind === 'disclosure' ? 'Disclosure' : 'Added'}
                  </span>
                  <span className="font-mono">{f.name}</span>
                  {f.uploaded_at && (
                    <span className="text-zinc-400">
                      {new Date(f.uploaded_at).toLocaleDateString()}
                    </span>
                  )}
                  {f.status !== 'indexed' && (
                    <span className="text-amber-700">being read in</span>
                  )}
                  {newMaterial.includes(f.name) && (
                    <span className="text-sky-700">not in the digest yet</span>
                  )}
                </li>
              ))}
            </ul>
          )}

          {confirmDiscard && (
            <div className="flex flex-wrap items-center gap-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
                 data-testid="intake-confirm-discard">
              <span>
                Reading it again from the disclosure discards your
                corrections to {(editedFields.length
                  ? editedFields : Object.keys(unsaved))
                  .map((f) => FIELD_LABEL[f] || f).join(', ')}.
                To keep them, resynthesize instead.
              </span>
              <button type="button" className={outlineBtn}
                      data-testid="intake-confirm-discard-yes"
                      onClick={() => runDigest(true)}>
                Read it again anyway
              </button>
              <button type="button" className={outlineBtn}
                      onClick={() => setConfirmDiscard(false)}>
                Keep my corrections
              </button>
            </div>
          )}

          {savedDigest && (
            <div className="space-y-3 rounded-md border border-zinc-200 bg-white p-4">
              {status.steps.idf.stale && (
                <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
                     data-testid="intake-digest-stale">
                  The disclosure was replaced after this was read, so it
                  still describes the old one. Read it again.
                </div>
              )}
              {!status.steps.idf.stale && newMaterial.length > 0 && (
                <div className="rounded border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900"
                     data-testid="intake-digest-new-material">
                  Added since this was read: {newMaterial.join(', ')}.
                  Resynthesize to fold it in — your corrections are kept.
                </div>
              )}
              {DIGEST_FIELDS.map((field) => (
                <label key={field} className="block">
                  <span className="text-[11px] font-medium uppercase tracking-wide text-zinc-500">
                    {field}
                    {editedFields.includes(field) && (
                      <span className="ml-2 normal-case tracking-normal text-sky-700"
                            data-testid={`intake-digest-edited-${field}`}>
                        · your correction
                      </span>
                    )}
                  </span>
                  <textarea
                    className={inputClass + ' mt-1'}
                    rows={3}
                    data-testid={`intake-digest-${field}`}
                    value={drafts[field] ?? savedDigest[field]}
                    onChange={(e) => {
                      const v = e.target.value;
                      setDrafts((d) => ({ ...d, [field]: v }));
                    }}
                    onBlur={(e) => {
                      if (e.target.value !== savedDigest[field]) {
                        void saveDigestField(field, e.target.value);
                      }
                    }}
                  />
                </label>
              ))}
              <div>
                <span className="text-[11px] font-medium uppercase tracking-wide text-zinc-500">
                  Technical features
                  {editedFields.includes('technical_features') && (
                    <span className="ml-2 normal-case tracking-normal text-sky-700"
                          data-testid="intake-digest-edited-technical_features">
                      · your correction
                    </span>
                  )}
                </span>
                <div className="mt-1 flex flex-wrap items-center gap-1.5"
                     data-testid="intake-digest-features">
                  {savedDigest.technical_features.map((f, i) => (
                    <span key={f}
                          className="inline-flex items-center gap-1 text-[11px] pl-2 pr-1 py-0.5 rounded-full bg-zinc-100 border border-zinc-200 text-zinc-700">
                      {f}
                      <button
                        type="button"
                        aria-label={`Remove ${f}`}
                        data-testid={`intake-feature-remove-${i}`}
                        className="rounded-full px-1 text-zinc-400 hover:bg-zinc-200 hover:text-zinc-700 disabled:opacity-50"
                        disabled={busy !== null}
                        onClick={() => void saveFeatures(
                          savedDigest.technical_features.filter((x) => x !== f),
                        )}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                  {savedDigest.technical_features.length === 0 && (
                    <span className="text-xs text-zinc-500">none found</span>
                  )}
                  <input
                    className="h-6 w-44 rounded border border-zinc-300 bg-white px-2 text-[11px] focus:outline-none focus:ring-2 focus:ring-zinc-300"
                    placeholder="Add a feature, then Enter"
                    data-testid="intake-feature-add"
                    value={featureDraft}
                    disabled={busy !== null}
                    onChange={(e) => setFeatureDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addFeature();
                      }
                    }}
                  />
                </div>
                <p className="mt-1 text-[11px] text-zinc-500">
                  These are what the final check compares the finished draft
                  against, to say what the disclosure covered and the draft
                  did not.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-3 border-t border-zinc-100 pt-3">
                <button
                  type="button"
                  className={primaryBtn}
                  data-testid="intake-resynthesize"
                  disabled={busy !== null || !canResynthesize
                    || status.steps.idf.indexed === 0}
                  // Keep focus in the field being typed: its blur-save
                  // would mark the page busy and disable this button
                  // before the click lands. The unsaved text goes with
                  // the request instead.
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => void resynthesize()}
                  title={canResynthesize
                    ? 'Re-read the disclosure and everything the client added, '
                      + 'with your corrections taken as authoritative'
                    : 'Correct a field above, or add information from the '
                      + 'client, first'}
                >
                  {busy === 'resynthesize' ? 'Resynthesizing…' : 'Resynthesize'}
                </button>
                <span className="text-[11px] text-zinc-500">
                  Re-reads the disclosure and everything the client added.
                  Your corrections are kept word for word; the rest of the
                  digest, the technical features and the technology area are
                  brought into line with them. Sections already drafted are
                  not rewritten.
                </span>
              </div>
            </div>
          )}

          <div className="flex justify-end">
            <button type="button" className={outlineBtn}
                    onClick={() => setStep('claims')}>
              Next: claims →
            </button>
          </div>
        </section>
      )}

      {/* ── Step 2 — claims ─────────────────────────────────────── */}
      {step === 'claims' && status && (
        <section className="space-y-3" data-testid="intake-claims">
          {status.steps.claims.done && (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900"
                 data-testid="intake-claims-done">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span>
                  {status.steps.claims.total} claims confirmed and frozen
                  {' '}({status.steps.claims.independent} independent,
                  {' '}{status.steps.claims.dependent} dependent). The
                  specification will be written against exactly these.
                </span>
                {!revising && (
                  <button type="button" className={outlineBtn}
                          data-testid="intake-revise-claims"
                          disabled={busy !== null}
                          onClick={startRevising}
                          title="The client sent amended or extra claims. Compare them with these, claim by claim, before anything changes.">
                    Revise with the client&apos;s changes
                  </button>
                )}
              </div>
            </div>
          )}

          {status.steps.claims.done && !revising
            && status.steps.claims.last_revision && (
            <LastRevision record={status.steps.claims.last_revision} />
          )}

          {(!status.steps.claims.done || revising) && (
            <>
              <p className="text-sm text-zinc-600">
                {revising
                  ? 'Upload or paste the client\'s revised claim set — the '
                    + 'whole set, as it should now read. It is compared with '
                    + 'the confirmed claims before anything changes.'
                  : 'Upload or paste the claim set. It is read as written — '
                    + 'the numbering, the dependencies and the categories come '
                    + 'from the text, and anything that cannot be read with '
                    + 'certainty is refused rather than guessed.'}
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <input
                  ref={fileRef}
                  type="file"
                  accept=".docx,.pdf,.txt,.md"
                  className="hidden"
                  data-testid="intake-claims-file"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) void parseFile(f);
                    e.target.value = '';
                  }}
                />
                <button type="button" className={outlineBtn}
                        disabled={busy !== null}
                        onClick={() => fileRef.current?.click()}>
                  {busy === 'parse' ? 'Reading…' : 'Upload a claim set'}
                </button>
                <span className="text-xs text-zinc-500">
                  DOCX, PDF or text — or paste below
                </span>
              </div>
              <textarea
                className={inputClass}
                rows={8}
                placeholder={'1. A system for …, comprising:\n…\n\n2. The system of claim 1, wherein …'}
                data-testid="intake-claims-text"
                value={claimsText}
                onChange={(e) => setClaimsText(e.target.value)}
              />
              <div className="flex items-center gap-2">
                <button type="button" className={outlineBtn}
                        disabled={busy !== null || !claimsText.trim()}
                        data-testid="intake-parse-claims"
                        onClick={parseText}>
                  {busy === 'parse' ? 'Reading…' : 'Read the claims'}
                </button>
                {revising && (
                  <button type="button" className={outlineBtn}
                          data-testid="intake-revise-cancel"
                          disabled={busy !== null}
                          onClick={stopRevising}>
                    Cancel
                  </button>
                )}
              </div>
            </>
          )}

          {parsed && !parsed.ok && (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800"
                 data-testid="intake-claims-errors">
              <div className="font-medium">
                This claim set could not be read with certainty, so nothing
                was written.
              </div>
              <ul className="mt-1 ml-4 list-disc">
                {parsed.errors.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            </div>
          )}

          {parsed && parsed.ok && (
            <div className="space-y-2" data-testid="intake-claims-preview">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-zinc-700">
                  {parsed.counts.total} claims read —
                  {' '}{parsed.counts.independent} independent,
                  {' '}{parsed.counts.dependent} dependent
                  {parsed.source_filename ? ` from ${parsed.source_filename}` : ''}
                </span>
                <button type="button" className={outlineBtn}
                        disabled={busy !== null}
                        data-testid="intake-qc-claims"
                        onClick={runQc}
                        title="Optional: check the claim set for technical correctness, antecedent basis, numbering and dependency. It reports; it never rewrites.">
                  {busy === 'qc' ? 'Checking…' : 'Check the claims (optional)'}
                </button>
                {revising ? (
                  <button type="button" className={primaryBtn}
                          disabled={busy !== null}
                          data-testid="intake-revise-compare"
                          onClick={compareRevision}>
                    {busy === 'revise-preview' ? 'Comparing…'
                      : 'Compare with the confirmed claims'}
                  </button>
                ) : (
                  <button type="button" className={primaryBtn}
                          disabled={busy !== null}
                          data-testid="intake-commit-claims"
                          onClick={commitClaims}>
                    {busy === 'commit' ? 'Confirming…' : 'Confirm and freeze'}
                  </button>
                )}
              </div>

              {revising && revision && (
                <RevisionReport
                  revision={revision}
                  busy={busy}
                  onApply={applyRevision}
                />
              )}

              {qc !== null && (
                <div className="rounded-md border border-zinc-200 bg-white p-3 text-sm"
                     data-testid="intake-qc-findings">
                  {qc.length === 0 ? (
                    <span className="text-emerald-800">
                      The check found nothing to report.
                    </span>
                  ) : (
                    <>
                      <div className="font-medium text-zinc-800">
                        {qc.length} finding{qc.length === 1 ? '' : 's'} — advisory.
                        Fix them in the source and re-upload, or confirm as they are.
                      </div>
                      <ul className="mt-1 space-y-1">
                        {qc.map((f, i) => (
                          <li key={i} className="text-[13px] text-zinc-700">
                            <span className="font-medium">
                              {f.claim_number ? `Claim ${f.claim_number}` : 'Claim set'}
                              {f.severity ? ` · ${f.severity}` : ''}:
                            </span>{' '}
                            {String(f.message ?? '')}
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
              )}

              <div className="rounded-md border border-zinc-200 bg-white divide-y divide-zinc-100">
                {parsed.claims.map((c) => (
                  <ClaimRow key={c.claim_number} claim={c} />
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      {/* ── Step 3 — drawings ───────────────────────────────────── */}
      {step === 'drawings' && status && (
        <section className="space-y-3" data-testid="intake-drawings">
          {!status.steps.claims.done ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              Confirm the claims first — the drawings open once they are
              frozen.
            </div>
          ) : (
            <>
              <p className="text-sm text-zinc-600">
                Add the figures, give each one a label and a line saying
                what it shows, and register the reference signs they use.
                Editing a drawing itself is the Drawing workbench's job,
                one click away.
              </p>
              <div className="flex flex-wrap items-center gap-2 text-xs text-zinc-600">
                <span data-testid="intake-drawing-counts">
                  {status.steps.drawings.figures} figure
                  {status.steps.drawings.figures === 1 ? '' : 's'} ·
                  {' '}{status.steps.drawings.frozen_figures} confirmed ·
                  {' '}{status.steps.drawings.numerals} reference sign
                  {status.steps.drawings.numerals === 1 ? '' : 's'}
                </span>
                {onOpenDrawings && (
                  <button type="button" className={outlineBtn}
                          data-testid="intake-open-drawings"
                          onClick={onOpenDrawings}>
                    Open the Drawing workbench ↗
                  </button>
                )}
              </div>

              {/* Disclosure images not yet in the drawings. Offered
                  rather than left to be discovered, because a figure
                  left behind simply will not be in the specification. */}
              {!!figures?.candidates.length && (
                <div className="rounded-md border border-sky-200 bg-sky-50/60 p-4 space-y-2"
                     data-testid="intake-candidates">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="text-sm font-medium text-zinc-800">
                      {figures.candidates.length} image
                      {figures.candidates.length === 1 ? '' : 's'} in the
                      disclosure {figures.candidates.length === 1 ? 'is' : 'are'}
                      {' '}not in the drawings yet
                    </div>
                    <div className="flex items-center gap-2">
                      <button type="button" className={outlineBtn}
                              disabled={busy !== null || !picked.length}
                              data-testid="intake-add-picked"
                              onClick={() => importPicked(picked)}>
                        {busy === 'import' ? 'Adding…' : `Add ${picked.length} selected`}
                      </button>
                      <button type="button" className={primaryBtn}
                              disabled={busy !== null}
                              data-testid="intake-add-all-candidates"
                              onClick={() => importPicked(
                                figures.candidates.map((c) => c.id),
                              )}>
                        Add all
                      </button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {figures.candidates.map((c) => {
                      const on = picked.includes(c.id);
                      return (
                        <button
                          key={c.id}
                          type="button"
                          data-testid={`intake-candidate-${c.id}`}
                          aria-pressed={on}
                          onClick={() => setPicked((p) => (
                            on ? p.filter((x) => x !== c.id) : [...p, c.id]
                          ))}
                          className={
                            'w-28 rounded border p-1 text-left '
                            + (on
                              ? 'border-sky-500 ring-2 ring-sky-200 bg-white'
                              : 'border-zinc-200 bg-white hover:border-zinc-400')
                          }
                        >
                          <img
                            src={specIntakeImageUrl(c.thumb_url)}
                            alt={c.label || `Disclosure image ${c.id}`}
                            className="h-20 w-full object-contain bg-zinc-50"
                          />
                          <div className="mt-1 truncate text-[11px] text-zinc-600">
                            {c.label || `Image ${c.id}`}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* The figures themselves. */}
              <div className="rounded-md border border-zinc-200 bg-white p-4 space-y-3"
                   data-testid="intake-figure-list">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="text-sm font-medium text-zinc-800">
                    The figures
                  </div>
                  <div>
                    <input
                      ref={figFileRef}
                      type="file"
                      accept="image/*,.pdf"
                      className="hidden"
                      data-testid="intake-figure-file"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) void uploadFigure(f);
                        e.target.value = '';
                      }}
                    />
                    <button type="button" className={outlineBtn}
                            disabled={busy !== null}
                            data-testid="intake-upload-figure"
                            onClick={() => figFileRef.current?.click()}>
                      {busy === 'fig-upload' ? 'Adding…' : 'Add a drawing'}
                    </button>
                  </div>
                </div>
                {figures && figures.figures.length === 0 && (
                  <p className="text-[13px] text-zinc-500"
                     data-testid="intake-no-figures">
                    No figures yet. The specification needs at least one —
                    add them from the disclosure above, or upload them.
                  </p>
                )}
                <div className="divide-y divide-zinc-100">
                  {(figures?.figures || []).map((f) => (
                    <FigureRow
                      key={f.id}
                      figure={f}
                      busy={busy}
                      onSave={(body) => saveFigure(f.id, body)}
                      onDiscard={() => discardFigure(f)}
                      onReadSigns={() => readSigns(f)}
                    />
                  ))}
                </div>
              </div>

              <div className="rounded-md border border-zinc-200 bg-white p-4 space-y-2">
                <div className="text-sm font-medium text-zinc-800">
                  Reference signs
                </div>
                <p className="text-xs text-zinc-500">
                  Read them off each drawing above, or paste the legend
                  that came with them — one numeral and its label per
                  line. This is what the description uses to chip
                  numerals, and what refuses a reference sign the drawings
                  do not have.
                </p>
                <textarea
                  className={inputClass}
                  rows={5}
                  placeholder={'100 turbine unit\n102 runner\n104 enclosed storage'}
                  data-testid="intake-legend-text"
                  value={legendText}
                  onChange={(e) => setLegendText(e.target.value)}
                />
                <div className="flex items-center gap-2">
                  <button type="button" className={outlineBtn}
                          disabled={busy !== null || !legendText.trim()}
                          data-testid="intake-parse-legend"
                          onClick={parseLegend}>
                    {busy === 'legend' ? 'Reading…' : 'Read the legend'}
                  </button>
                  {signs && signs.some((s) => s.label.trim() && s.numeral.trim()) && (
                    <button type="button" className={primaryBtn}
                            disabled={busy !== null}
                            data-testid="intake-commit-legend"
                            onClick={commitLegend}>
                      {busy === 'legend-commit'
                        ? 'Registering…'
                        : `Register ${registrable} sign`
                          + `${registrable === 1 ? '' : 's'}`}
                    </button>
                  )}
                </div>
                {signs && signs.length > 0 && (
                  <div className="space-y-1" data-testid="intake-legend-preview">
                    {signsFrom && (
                      <div className="text-[11px] text-zinc-500">
                        From {signsFrom}. Correct a name or a number here if
                        it was misread. Nothing is written until you
                        register them.
                      </div>
                    )}
                    <div className="space-y-1">
                      {signs.map((s) => (
                        <div
                          key={s.key}
                          data-testid={`intake-sign-${s.key.replace(/-\d+$/, '')}`}
                          className={
                            'flex flex-wrap items-center gap-2 rounded border px-2 py-1 text-[12px] '
                            + (!s.label.trim()
                              ? 'bg-zinc-50 border-dashed border-zinc-300'
                              : s.conflict
                                ? 'bg-amber-50 border-amber-300'
                                : 'bg-sky-50/60 border-sky-200')
                          }
                        >
                          <input
                            className="w-16 rounded border border-zinc-300 bg-white px-1.5 py-0.5 font-mono text-[12px]"
                            aria-label="Reference numeral"
                            data-testid={`intake-sign-numeral-${s.key}`}
                            value={s.numeral}
                            onChange={(e) => editSign(s.key, { numeral: e.target.value })}
                          />
                          <input
                            className="min-w-0 flex-1 rounded border border-zinc-300 bg-white px-1.5 py-0.5 text-[12px]"
                            aria-label={`Name of reference sign ${s.numeral}`}
                            placeholder="needs a name"
                            data-testid={`intake-sign-label-${s.key}`}
                            value={s.label}
                            onChange={(e) => editSign(s.key, { label: e.target.value })}
                          />
                          <span className="text-[11px] text-zinc-500">
                            {!s.label.trim()
                              ? '— needs a name'
                              : s.conflict
                                ? `already registered differently (was: ${s.existing_label})`
                                : s.known ? 'already registered' : ''}
                          </span>
                          <button
                            type="button"
                            className="text-[11px] text-zinc-500 hover:text-zinc-900"
                            data-testid={`intake-sign-remove-${s.key}`}
                            aria-label={`Leave out reference sign ${s.numeral}`}
                            onClick={() => dropSign(s.key)}
                          >
                            Leave out
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {similar.length > 0 && signs && signs.length > 0 && (
                  <SimilarSignsNote groups={similar} testId="intake-similar-pending" />
                )}
              </div>

              <div className="rounded-md border border-zinc-200 bg-white p-4 space-y-2">
                <div className="text-sm font-medium text-zinc-800">
                  Confirm the figures
                </div>
                <p className="text-xs text-zinc-500">
                  Each figure needs a one-line description before it can be
                  confirmed — the brief description of the drawings is
                  written from it.
                </p>
                {(status.steps.drawings.similar_signs || []).length > 0 && (
                  <SimilarSignsNote
                    groups={status.steps.drawings.similar_signs || []}
                    testId="intake-similar-signs"
                  />
                )}
                {status.steps.drawings.needs_description.length > 0 && (
                  <div className="text-[13px] text-amber-900 bg-amber-50 border border-amber-200 rounded px-2 py-1"
                       data-testid="intake-needs-description">
                    Waiting on a description:
                    {' '}{status.steps.drawings.needs_description.join(', ')}.
                    Add it against the figure above.
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <button type="button" className={primaryBtn}
                          disabled={busy !== null || status.steps.drawings.figures === 0}
                          data-testid="intake-freeze-all"
                          onClick={() => freezeAll(false)}>
                    {busy === 'freeze' ? 'Confirming…' : 'Confirm the figures'}
                  </button>
                  <button type="button" className={outlineBtn}
                          disabled={busy !== null || status.steps.drawings.figures === 0}
                          data-testid="intake-freeze-all-anyway"
                          onClick={() => freezeAll(true)}
                          title="Confirm even where the drawing checks reported something">
                    Confirm anyway
                  </button>
                </div>
                {freezeReport && (
                  <div className="text-[13px] space-y-1"
                       data-testid="intake-freeze-report">
                    {freezeReport.frozen.length > 0 && (
                      <div className="text-emerald-800">
                        Confirmed: {freezeReport.frozen.join(', ')}
                      </div>
                    )}
                    {freezeReport.failed.map((f, i) => (
                      <div key={i} className="text-red-800">
                        {f.figure}: {f.reason}
                        {(f.issue_lines || []).length > 0 && (
                          <ul className="ml-4 list-disc text-[12px] text-zinc-700"
                              data-testid={`intake-freeze-issues-${i}`}>
                            {(f.issue_lines || []).map((line) => (
                              <li key={line}>{line}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex justify-end">
                <button type="button" className={outlineBtn}
                        onClick={() => setStep('ready')}>
                  Next: start →
                </button>
              </div>
            </>
          )}
        </section>
      )}

      {/* ── Step 4 — start ──────────────────────────────────────── */}
      {step === 'ready' && status && (
        <section className="space-y-3" data-testid="intake-ready">
          <div className="rounded-md border border-zinc-200 bg-white p-4 space-y-2">
            <div className="text-sm font-medium text-zinc-800">
              What the specification will be written from
            </div>
            <ul className="text-[13px] text-zinc-700 space-y-0.5">
              <li>
                {status.steps.claims.total} claims
                {status.steps.claims.frozen ? ', frozen' : ''}
              </li>
              <li>
                {status.steps.drawings.frozen_figures} confirmed figure
                {status.steps.drawings.frozen_figures === 1 ? '' : 's'}
              </li>
              <li>
                {status.steps.drawings.numerals} reference sign
                {status.steps.drawings.numerals === 1 ? '' : 's'}
              </li>
              <li>
                {status.steps.idf.has_digest
                  ? 'the disclosure, read'
                  : 'no disclosure digest'}
                {status.steps.idf.tech_bucket
                  ? ` · ${status.steps.idf.tech_bucket.toLowerCase()}` : ''}
              </li>
            </ul>
          </div>

          {status.ready.blocking.length > 0 && (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800"
                 data-testid="intake-blocking">
              <div className="font-medium">Still needed</div>
              <ul className="mt-1 ml-4 list-disc">
                {status.ready.blocking.map((b, i) => <li key={i}>{b}</li>)}
              </ul>
            </div>
          )}
          {status.ready.advisory.length > 0 && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
                 data-testid="intake-advisory">
              <div className="font-medium">Worth knowing before you start</div>
              <ul className="mt-1 ml-4 list-disc">
                {status.ready.advisory.map((a, i) => <li key={i}>{a}</li>)}
              </ul>
            </div>
          )}

          <button
            type="button"
            className={primaryBtn}
            data-testid="intake-open-spec"
            disabled={!status.ready.done || !onOpenSpec}
            onClick={() => onOpenSpec?.()}
            title={status.ready.done
              ? 'Open the specification'
              : 'Finish the steps above first'}
          >
            Start drafting the specification →
          </button>
        </section>
      )}

      <AppFooter className="mt-8" />
    </div>
  );
}

/** NB-36 — numerals that name one element ("vapour treatment" 220 and
 * "vapour treatment step" 230). Said where the signs are registered
 * and where the figures are confirmed; the drafter decides. */
function SimilarSignsNote({
  groups, testId,
}: {
  groups: SpecIntakeSimilarSigns[];
  testId: string;
}) {
  return (
    <div className="text-[13px] text-amber-900 bg-amber-50 border border-amber-200 rounded px-2 py-1 space-y-0.5"
         data-testid={testId}>
      {groups.map((g) => (
        <div key={g.numerals.join('-')}>{g.message}</div>
      ))}
    </div>
  );
}

/** One figure, as the intake needs it: the drawing beside the two
 * fields that decide whether it can be confirmed.
 *
 * The label and the description are held locally and saved on blur —
 * the drafter types a sentence, not a keystroke, and a request per
 * keystroke would fight them. A frozen figure is shown read-only
 * rather than hidden, because "already confirmed" is information.
 *
 * Everything here is confirmation. Cropping, redrawing, placing
 * numerals on the canvas: all of that is the Drawing workbench, and a
 * second half-built copy of it would be worse than the link. */
function FigureRow({
  figure, busy, onSave, onDiscard, onReadSigns,
}: {
  figure: SpecIntakeFigure;
  busy: string | null;
  onSave: (body: { fig_label?: string; description?: string }) => void;
  onDiscard: () => void;
  onReadSigns: () => void;
}) {
  const [label, setLabel] = useState(figure.fig_label);
  const [description, setDescription] = useState(figure.description);

  // The server is the truth: after a save, an import or a freeze the
  // row is refetched, and what it says wins over what is in the box.
  useEffect(() => { setLabel(figure.fig_label); }, [figure.fig_label]);
  useEffect(() => { setDescription(figure.description); }, [figure.description]);

  const disabled = busy !== null || figure.is_frozen;

  return (
    <div className="flex gap-3 py-3" data-testid={`intake-figure-${figure.id}`}>
      <img
        src={specIntakeImageUrl(figure.thumb_url)}
        alt={figure.fig_label || 'Figure'}
        className="h-24 w-24 shrink-0 rounded border border-zinc-200 bg-zinc-50 object-contain"
      />
      <div className="min-w-0 flex-1 space-y-1.5">
        <div className="flex flex-wrap items-center gap-2">
          <input
            className={
              'w-32 rounded border border-zinc-300 bg-white px-2 py-1 '
              + 'text-[13px] text-zinc-800 disabled:bg-zinc-50 '
              + 'focus:outline-none focus:ring-2 focus:ring-zinc-300'
            }
            data-testid={`intake-figure-label-${figure.id}`}
            value={label}
            disabled={disabled}
            onChange={(e) => setLabel(e.target.value)}
            onBlur={() => {
              if (label !== figure.fig_label) onSave({ fig_label: label });
            }}
          />
          {figure.is_frozen && (
            <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800">
              confirmed
            </span>
          )}
          <span className="text-[11px] text-zinc-500">
            {figure.numerals_count} reference sign
            {figure.numerals_count === 1 ? '' : 's'} placed
          </span>
          <span className="flex-1" />
          <button
            type="button"
            className={outlineBtn}
            data-testid={`intake-read-signs-${figure.id}`}
            disabled={busy !== null}
            onClick={onReadSigns}
            title="Read the reference signs printed on this drawing. It reports; nothing is registered until you confirm."
          >
            {busy === `signs-${figure.id}` ? 'Reading…' : 'Read its signs'}
          </button>
          {!figure.is_frozen && (
            <button
              type="button"
              className={outlineBtn}
              data-testid={`intake-discard-${figure.id}`}
              disabled={busy !== null}
              onClick={onDiscard}
              title="Remove this figure from the drawings. The disclosure image it came from is untouched."
            >
              Discard
            </button>
          )}
        </div>
        <textarea
          className={inputClass}
          rows={2}
          placeholder="What this figure shows, in one line — this becomes the brief description of the drawings."
          data-testid={`intake-figure-description-${figure.id}`}
          value={description}
          disabled={disabled}
          onChange={(e) => setDescription(e.target.value)}
          onBlur={() => {
            if (description !== figure.description) onSave({ description });
          }}
        />
        {!figure.is_frozen && !description.trim() && (
          <div className="text-[11px] text-amber-800">
            Needs a description before it can be confirmed.
          </div>
        )}
      </div>
    </div>
  );
}

function ParagraphList({ paragraphs }: { paragraphs: SpecIntakeRevisionParagraph[] }) {
  if (!paragraphs.length) return null;
  return (
    <span className="text-[12px] text-zinc-600">
      {' '}Review:{' '}
      {paragraphs.map((p, i) => (
        <span key={p.paragraph_id}>
          {i ? ', ' : ''}
          {p.section_title} ¶{p.paragraph_number}
        </span>
      ))}
    </span>
  );
}

/** The client's revised set compared with the confirmed claims. Nothing
 * is written until Apply. */
function RevisionReport({
  revision, busy, onApply,
}: {
  revision: SpecIntakeRevision;
  busy: string | null;
  onApply: () => void;
}) {
  const r = revision;
  return (
    <div className="space-y-2 rounded-md border border-zinc-200 bg-white p-3 text-sm"
         data-testid="intake-revision">
      {!r.has_changes ? (
        <div className="text-zinc-700">
          This is the confirmed claim set as it stands — nothing to revise.
        </div>
      ) : (
        <>
          <div className="text-zinc-700">
            {r.unchanged} claim{r.unchanged === 1 ? '' : 's'} unchanged.
          </div>
          {r.renumbered.length > 0 && (
            <div data-testid="intake-revision-renumbered">
              <span className="font-medium">Renumbered</span>, wording
              unchanged:{' '}
              {r.renumbered.map((m) => `${m.from} → ${m.to}`).join(', ')}.
              The draft built on them moves with them.
            </div>
          )}
          {r.changed.length > 0 && (
            <div data-testid="intake-revision-changed">
              <div className="font-medium">Changed</div>
              <ul className="mt-1 space-y-2">
                {r.changed.map((c) => (
                  <li key={c.claim_number} className="text-[13px]">
                    <div>
                      Claim {c.claim_number}
                      {c.previous_number !== c.claim_number
                        ? ` (was ${c.previous_number})` : ''}
                      <ParagraphList paragraphs={c.paragraphs} />
                    </div>
                    <div className="mt-0.5 text-zinc-500 line-through decoration-zinc-300">
                      {c.old_text}
                    </div>
                    <div className="text-zinc-800">{c.new_text}</div>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {r.added.length > 0 && (
            <div data-testid="intake-revision-added">
              <span className="font-medium">New</span>: claim
              {r.added.length === 1 ? ' ' : 's '}
              {r.added.map((a) => a.claim_number).join(', ')}. Confirmed
              with the rest; the Specification page shows them as not yet
              covered.
            </div>
          )}
          {r.removed.length > 0 && (
            <div data-testid="intake-revision-removed">
              <span className="font-medium">Removed</span>:{' '}
              {r.removed.map((x) => (
                <span key={x.claim_number}>
                  claim {x.claim_number}
                  <ParagraphList paragraphs={x.paragraphs} />{' '}
                </span>
              ))}
            </div>
          )}
          {r.numerals_to_check.length > 0 && (
            <div className="text-amber-900" data-testid="intake-revision-numerals">
              Reference signs bound to wording that has gone:{' '}
              {r.numerals_to_check.map((n) =>
                `${n.numeral} ${n.label} (“${n.term}”, claim ${n.claim_number})`,
              ).join('; ')}.
            </div>
          )}
          {r.blocked && (
            <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-red-800"
                 data-testid="intake-revision-blocked">
              {r.blocked_reason}
            </div>
          )}
          <div className="flex items-center gap-3 pt-1">
            <button type="button" className={primaryBtn}
                    data-testid="intake-revise-apply"
                    disabled={busy !== null || r.blocked}
                    onClick={onApply}>
              {busy === 'revise-apply' ? 'Applying…' : 'Apply the revision'}
            </button>
            <span className="text-[11px] text-zinc-500">
              Paragraphs already written are not rewritten — the ones listed
              are for you to review.
            </span>
          </div>
        </>
      )}
    </div>
  );
}

/** What the last revision asked the drafter to look at. */
function LastRevision({
  record,
}: {
  record: NonNullable<SpecIntakeStatus['steps']['claims']['last_revision']>;
}) {
  const toReview = record.changed.filter((c) => c.paragraphs.length);
  return (
    <div className="rounded-md border border-zinc-200 bg-white p-3 text-[13px] text-zinc-700"
         data-testid="intake-last-revision">
      <span className="font-medium">
        Revised {record.at ? new Date(record.at).toLocaleDateString() : ''}
      </span>
      {record.added.length > 0 && <> · new: {record.added.join(', ')}</>}
      {record.changed.length > 0 && (
        <> · changed: {record.changed.map((c) => c.claim_number).join(', ')}</>
      )}
      {record.removed.length > 0 && <> · removed: {record.removed.join(', ')}</>}
      {record.renumbered.length > 0 && (
        <> · renumbered: {record.renumbered.map((m) => `${m.from}→${m.to}`).join(', ')}</>
      )}
      {toReview.length > 0 && (
        <ul className="mt-1 ml-4 list-disc">
          {toReview.map((c) => (
            <li key={c.claim_number}>
              Claim {c.claim_number}:
              <ParagraphList paragraphs={c.paragraphs} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** One row of the claim preview. Read-only by design: this page
 * confirms what was parsed, it does not edit claims. A claim that is
 * wrong is wrong in the source — fix it there and re-upload, so the
 * file and the matter never disagree. */
function ClaimRow({ claim }: { claim: SpecIntakeParsedClaim }) {
  return (
    <div className="px-3 py-2" data-testid={`intake-claim-${claim.claim_number}`}>
      <div className="flex items-start gap-2">
        <span className="mt-0.5 shrink-0 text-[10px] font-mono px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-500">
          {claim.claim_number}
        </span>
        <div className="min-w-0">
          <div className="text-[13px] leading-5 text-zinc-800">
            {claim.claim_text}
          </div>
          <div className="mt-0.5 text-[11px] text-zinc-500">
            {claim.claim_type}
            {claim.parent_claim_number
              ? ` of claim ${claim.parent_claim_number}` : ''}
            {claim.depends_on.length > 1
              ? ` (and ${claim.depends_on.slice(1).join(', ')})` : ''}
            {' · '}{claim.category}
          </div>
          {claim.notes.map((n, i) => (
            <div key={i}
                 className="mt-1 text-[12px] text-amber-900 bg-amber-50 border border-amber-200 rounded px-2 py-1">
              {n}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
